import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuizzes, usePublishQuiz, useArchiveQuiz } from "@/hooks/useQuizzes";
import { Button } from "@/components/ui/button";
import { QuizStatusBadge } from "@/components/quizzes/QuizStatusBadge";
import { QuizFormDialog } from "@/components/quizzes/QuizFormDialog";
import { DeleteQuizDialog } from "@/components/quizzes/DeleteQuizDialog";
import type { Quiz } from "@/types/quiz";

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function LoadingSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="h-12 animate-pulse rounded bg-muted" />
      ))}
    </div>
  );
}

export function QuizzesPage() {
  const navigate = useNavigate();
  const { data: quizzes, isLoading, isError, error, refetch } = useQuizzes();
  const publishMutation = usePublishQuiz();
  const archiveMutation = useArchiveQuiz();

  const [formOpen, setFormOpen] = useState(false);
  const [editQuiz, setEditQuiz] = useState<Quiz | undefined>();
  const [deleteTarget, setDeleteTarget] = useState<Quiz | null>(null);

  const items = quizzes ?? [];

  function handleCreate() {
    setEditQuiz(undefined);
    setFormOpen(true);
  }

  function handleEdit(quiz: Quiz) {
    navigate(`/quizzes/${quiz.quizId}`);
  }

  function handleFormClose() {
    setFormOpen(false);
    setEditQuiz(undefined);
  }

  function handleDeleteClose() {
    setDeleteTarget(null);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">Quizzes</h1>
          {!isLoading && !isError && (
            <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
              {items.length} {items.length === 1 ? "Quiz" : "Quizzes"}
            </span>
          )}
        </div>
        <Button onClick={handleCreate}>+ Neues Quiz</Button>
      </div>

      {isLoading && <LoadingSkeleton />}

      {isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
          <p className="text-sm text-red-700">
            Quizzes konnten nicht geladen werden:{" "}
            {error instanceof Error ? error.message : "Unbekannter Fehler"}
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => refetch()}
          >
            Erneut versuchen
          </Button>
        </div>
      )}

      {!isLoading && !isError && items.length === 0 && (
        <div className="rounded-lg border border-dashed p-12 text-center">
          <p className="text-muted-foreground">
            Noch keine Quizzes vorhanden. Erstellen Sie Ihr erstes Quiz!
          </p>
          <Button variant="outline" className="mt-3" onClick={handleCreate}>
            Erstes Quiz erstellen
          </Button>
        </div>
      )}

      {!isLoading && !isError && items.length > 0 && (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                  Titel
                </th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                  Status
                </th>
                <th className="px-4 py-3 text-right font-medium text-muted-foreground">
                  Fragen
                </th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                  Erstellt
                </th>
                <th className="px-4 py-3 text-right font-medium text-muted-foreground" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {items.map((quiz) => (
                <tr key={quiz.quizId} className="hover:bg-muted/30">
                  <td className="px-4 py-3 font-medium">
                    <Link
                      to={`/quizzes/${quiz.quizId}`}
                      className="hover:underline"
                    >
                      {quiz.title}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <QuizStatusBadge status={quiz.status} />
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {quiz.questionCount}{" "}
                    {quiz.questionCount === 1 ? "Frage" : "Fragen"}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {formatDate(quiz.createdAt)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {quiz.status === "DRAFT" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={publishMutation.isPending}
                          onClick={() => publishMutation.mutate(quiz.quizId)}
                        >
                          Veröffentlichen
                        </Button>
                      )}
                      {quiz.status === "PUBLISHED" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={archiveMutation.isPending}
                          onClick={() => archiveMutation.mutate(quiz.quizId)}
                        >
                          Archivieren
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(quiz)}
                      >
                        Bearbeiten
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setDeleteTarget(quiz)}
                      >
                        Löschen
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <QuizFormDialog
        open={formOpen}
        onClose={handleFormClose}
        quiz={editQuiz}
      />

      <DeleteQuizDialog
        open={deleteTarget !== null}
        onClose={handleDeleteClose}
        quiz={deleteTarget}
      />
    </div>
  );
}
